import useNaverADsScript, { NaverADsType } from "@repo/hooks/lib/useNaverADsScript";
import useOrderStatus from "@repo/shopify/hooks/useOrderStatus";
import type { IOrdersEdges, IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import {
  OrderStatusButtonType,
  OrderStatusTrans,
  checkDrawOrder,
  orderHasTracking,
} from "@repo/shopify/src/utils/order";
import { transformDate } from "@repo/utils/src/number";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Status from "@components/atoms/Status";

import OrderStatusButton from "@molecules/OrderStatusButton";
import TrackingNumber from "@molecules/TrackingNumber";

import OrderProductsInfo from "@organisms/Account/OrderDetail/OrderProductsInfo";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

import { getStatusColor } from "@utils/order";

function OrderDetail() {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const { setMetaTag } = useMeta();
  const { customer } = useUser();
  const { tryAppendNaverADsScript } = useNaverADsScript();
  const { t } = useTranslation();
  const { showFooter } = useLayout();

  const order: { node: IOrdersEdgesNode } | null = useMemo(
    () =>
      (customer.orders as IOrdersEdges).edges.find(({ node }) =>
        node.id.startsWith(`gid://shopify/Order/${orderId}`),
      ) || null,
    [customer.orders, orderId],
  );

  tryAppendNaverADsScript({ type: NaverADsType.Join, value: "0" });

  const { activeButton, orderStatus } = useOrderStatus(order?.node, true);
  const orderNumber = order?.node.name.replace("#", "") || "";
  const cardInfo = useMemo(() => (order?.node.cardInfo?.value ? JSON.parse(order.node.cardInfo.value) : ""), [order]);

  const isDrawOrder = checkDrawOrder(order?.node);

  useEffect(() => {
    showFooter(true);
  }, []);

  useEffect(() => {
    if (!order) {
      navigate(URLS.NOT_FOUND);
      return;
    }

    setMetaTag({ title: `TUNE.KR - ORDER DETAIL` });
  }, [order, navigate, cardInfo]);

  return (
    <>
      {order ? (
        <section className="tune-column-4 pc:-mt-0 -mt-5 flex flex-col gap-10">
          <section className="flex flex-col gap-2.5">
            <span className="font-regular text-tuneGrey-400 text-sm">
              {order.node.processedAt ? transformDate(order.node.processedAt) : ""}
            </span>
            <div className="flex flex-col gap-1">
              <Status
                orderName={orderNumber}
                statusColor={getStatusColor(orderStatus)}
                statusName={t(OrderStatusTrans[orderStatus])}
              />
              {orderHasTracking(order.node) && <TrackingNumber order={order.node} />}
            </div>
            {activeButton !== OrderStatusButtonType.NONE && !checkDrawOrder(order.node) ? (
              <OrderStatusButton
                amount={order.node.totalPrice.amount}
                buttonType={activeButton}
                id={order.node.id}
                isDrawOrder={isDrawOrder}
                name={order.node.name}
              />
            ) : null}
          </section>
          <OrderProductsInfo order={order.node} />
        </section>
      ) : null}
    </>
  );
}

export default OrderDetail;
